import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import html2canvas from 'html2canvas';
import {
  Sparkle, DownloadSimple, Trash, Upload, ArrowsClockwise,
  TextT, ImageSquare, FolderOpen, FloppyDisk, CaretDown,
  TextAlignLeft, TextAlignCenter, TextAlignRight,
  Cursor, Clock, Eye
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { EarlyAccessBanner } from '../components/EarlyAccessBanner';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import { AssetPickerModal } from '../components/AssetPickerModal';
import { useUpgrade } from '../components/UpgradeSystem';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Fonts ── */
const FONTS = [
  { id: 'dm-sans', name: 'DM Sans', family: "'DM Sans', sans-serif" },
  { id: 'inter', name: 'Inter', family: "'Inter', sans-serif" },
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', serif" },
  { id: 'montserrat', name: 'Montserrat', family: "'Montserrat', sans-serif" },
  { id: 'oswald', name: 'Oswald', family: "'Oswald', sans-serif" },
  { id: 'bebas', name: 'Bebas Neue', family: "'Bebas Neue', sans-serif" },
  { id: 'poppins', name: 'Poppins', family: "'Poppins', sans-serif" },
  { id: 'space-grotesk', name: 'Space Grotesk', family: "'Space Grotesk', sans-serif" },
  { id: 'raleway', name: 'Raleway', family: "'Raleway', sans-serif" },
  { id: 'lora', name: 'Lora', family: "'Lora', serif" },
];
const fontOf = (id) => FONTS.find(f => f.id === id) || FONTS[0];

/* ── Color presets ── */
const COLORS = ['#ffffff','#f5f0e8','#1a1a1a','#000000','#c8f060','#ff4444','#f59e0b','#22d3ee','#a78bfa','#2563eb','#10b981','#ec4899'];

/* ── Layout presets ── */
const PRESETS = [
  {
    id: 'luxury', name: 'Luxury', layout: 'centered',
    bg: '#0c0a08', accent: '#d4a855', text: '#f5f0e8', sub: 'rgba(245,240,232,0.5)',
    headFont: 'playfair', headSize: 42, headWeight: '600', subFont: 'dm-sans',
    ctaBg: '#d4a855', ctaText: '#0c0a08',
    overlay: 'linear-gradient(180deg, transparent 30%, rgba(12,10,8,0.95) 70%)',
    positions: { headline:{x:50,y:40}, subtext:{x:50,y:56}, cta:{x:50,y:76}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'line',
  },
  {
    id: 'bold_sale', name: 'Bold Promo', layout: 'top-heavy',
    bg: '#c8f060', accent: '#080808', text: '#080808', sub: 'rgba(8,8,8,0.6)',
    headFont: 'bebas', headSize: 62, headWeight: '400', subFont: 'dm-sans',
    ctaBg: '#080808', ctaText: '#c8f060',
    overlay: 'linear-gradient(180deg, transparent 30%, rgba(200,240,96,0.95) 70%)',
    positions: { headline:{x:50,y:30}, subtext:{x:50,y:50}, cta:{x:50,y:78}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
    scales: { headline:1.1, subtext:1, cta:1.1, logo:1, productImg:1 },
    badge: 'SALE', decorStyle: 'corner',
  },
  {
    id: 'minimal', name: 'Clean Pro', layout: 'left-aligned',
    bg: '#fafafa', accent: '#111111', text: '#111111', sub: 'rgba(17,17,17,0.5)',
    headFont: 'inter', headSize: 38, headWeight: '700', subFont: 'dm-sans',
    ctaBg: '#111111', ctaText: '#ffffff',
    overlay: 'none',
    positions: { headline:{x:18,y:35}, subtext:{x:18,y:52}, cta:{x:18,y:72}, logo:{x:12,y:8}, productImg:{x:75,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'none',
  },
  {
    id: 'tech', name: 'Tech Dark', layout: 'centered',
    bg: '#08080f', accent: '#818cf8', text: '#f0f0ff', sub: 'rgba(240,240,255,0.45)',
    headFont: 'space-grotesk', headSize: 40, headWeight: '600', subFont: 'dm-sans',
    ctaBg: '#818cf8', ctaText: '#08080f',
    overlay: 'linear-gradient(180deg, transparent 30%, rgba(8,8,15,0.93) 70%)',
    positions: { headline:{x:50,y:40}, subtext:{x:50,y:56}, cta:{x:50,y:76}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'grid',
  },
  {
    id: 'neon_pink', name: 'Neon Pop', layout: 'top-heavy',
    bg: '#12061a', accent: '#ec4899', text: '#ffffff', sub: 'rgba(255,255,255,0.5)',
    headFont: 'montserrat', headSize: 44, headWeight: '800', subFont: 'dm-sans',
    ctaBg: '#ec4899', ctaText: '#ffffff',
    overlay: 'linear-gradient(180deg, transparent 25%, rgba(18,6,26,0.92) 60%)',
    positions: { headline:{x:50,y:32}, subtext:{x:50,y:50}, cta:{x:50,y:76}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
    scales: { headline:1.05, subtext:1, cta:1.05, logo:1, productImg:1 },
    badge: 'NEW', decorStyle: 'glow',
  },
  {
    id: 'forest', name: 'Organic', layout: 'centered',
    bg: '#0a1a0d', accent: '#10b981', text: '#ecfdf5', sub: 'rgba(236,253,245,0.5)',
    headFont: 'lora', headSize: 40, headWeight: '600', subFont: 'dm-sans',
    ctaBg: '#10b981', ctaText: '#0a1a0d',
    overlay: 'linear-gradient(180deg, transparent 30%, rgba(10,26,13,0.93) 70%)',
    positions: { headline:{x:50,y:40}, subtext:{x:50,y:56}, cta:{x:50,y:76}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'leaf',
  },
  {
    id: 'sunset', name: 'Sunset', layout: 'left-aligned',
    bg: '#1c0a05', accent: '#f97316', text: '#fff7ed', sub: 'rgba(255,247,237,0.5)',
    headFont: 'montserrat', headSize: 38, headWeight: '700', subFont: 'dm-sans',
    ctaBg: '#f97316', ctaText: '#ffffff',
    overlay: 'linear-gradient(135deg, rgba(28,10,5,0.85) 40%, transparent)',
    positions: { headline:{x:20,y:35}, subtext:{x:20,y:52}, cta:{x:20,y:72}, logo:{x:12,y:8}, productImg:{x:78,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'none',
  },
  {
    id: 'ocean', name: 'Ocean', layout: 'centered',
    bg: '#060e1a', accent: '#38bdf8', text: '#f0f9ff', sub: 'rgba(240,249,255,0.5)',
    headFont: 'oswald', headSize: 44, headWeight: '600', subFont: 'dm-sans',
    ctaBg: '#38bdf8', ctaText: '#060e1a',
    overlay: 'linear-gradient(180deg, transparent 30%, rgba(6,14,26,0.93) 70%)',
    positions: { headline:{x:50,y:40}, subtext:{x:50,y:56}, cta:{x:50,y:76}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'wave',
  },
  {
    id: 'cream_editorial', name: 'Editorial', layout: 'left-aligned',
    bg: '#f5f0e8', accent: '#1a1a1a', text: '#1a1a1a', sub: 'rgba(26,26,26,0.45)',
    headFont: 'playfair', headSize: 44, headWeight: '700', subFont: 'dm-sans',
    ctaBg: '#1a1a1a', ctaText: '#f5f0e8',
    overlay: 'none',
    positions: { headline:{x:18,y:38}, subtext:{x:18,y:55}, cta:{x:18,y:74}, logo:{x:12,y:8}, productImg:{x:80,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'serif-line',
  },
  {
    id: 'gradient_purple', name: 'Gradient', layout: 'centered',
    bg: '#0f0520', accent: '#a78bfa', text: '#ffffff', sub: 'rgba(255,255,255,0.5)',
    headFont: 'poppins', headSize: 40, headWeight: '700', subFont: 'dm-sans',
    ctaBg: 'linear-gradient(135deg, #a78bfa, #ec4899)', ctaText: '#ffffff',
    overlay: 'linear-gradient(180deg, transparent 25%, rgba(15,5,32,0.92) 65%)',
    positions: { headline:{x:50,y:38}, subtext:{x:50,y:55}, cta:{x:50,y:76}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
    scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
    badge: null, decorStyle: 'gradient-blur',
  },
];

const EXPORTS = [
  { id: 'square', label: '1080 x 1080', w: 1080, h: 1080, tag: 'Instagram / Facebook' },
  { id: 'story', label: '1080 x 1920', w: 1080, h: 1920, tag: 'Stories / Reels' },
  { id: 'landscape', label: '1200 x 628', w: 1200, h: 628, tag: 'Facebook / LinkedIn' },
];

/* ═══ Card Wrapper (matching Ad/Carousel pages) ═══ */
const Card = ({ title, badge, children, className = '' }) => (
  <div className={`rounded-xl p-5 ${className}`} style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
    {title && (
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{title}</h3>
        {badge && <span className="text-[9px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(200,240,96,0.1)', color: '#c8f060' }}>{badge}</span>}
      </div>
    )}
    {children}
  </div>
);

/* ═══ Field Input ═══ */
const FieldInput = ({ label, value, onChange, placeholder, testId, multiline }) => (
  <div className="mb-3">
    <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>{label}</label>
    {multiline ? (
      <textarea rows={2} data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
        style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
    ) : (
      <input data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg text-sm"
        style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
    )}
  </div>
);

/* ════════════════════════════════════════════════════ */

export default function FlyerCreatorPage() {
  const { checkCreditsBeforeAction, deductCreditsAfterExport, handleApiError } = useUpgrade();
  const [tab, setTab] = useState('create');
  const [preset, setPreset] = useState(PRESETS[0]);

  /* content */
  const [headline, setHeadline] = useState('');
  const [subtext, setSubtext] = useState('');
  const [cta, setCta] = useState('Learn more');
  const [productImg, setProductImg] = useState(null);
  const [logoImg, setLogoImg] = useState(null);

  /* typography */
  const [headFont, setHeadFont] = useState(preset.headFont);
  const [headSize, setHeadSize] = useState(preset.headSize);
  const [headWeight, setHeadWeight] = useState(preset.headWeight);
  const [headColor, setHeadColor] = useState(preset.text);
  const [headAlign, setHeadAlign] = useState('center');
  const [subColor, setSubColor] = useState(preset.sub);
  const [subSize, setSubSize] = useState(16);
  const [ctaBg, setCtaBg] = useState(preset.ctaBg);
  const [ctaTextColor, setCtaTextColor] = useState(preset.ctaText);

  /* drag / resize */
  const [positions, setPositions] = useState({ ...preset.positions });
  const [scales, setScales] = useState({ ...preset.scales });
  const [selected, setSelected] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null);
  const resizeStart = useRef(null);
  const canvasRef = useRef(null);

  /* misc */
  const [exportSize, setExportSize] = useState(EXPORTS[0]);
  const [exporting, setExporting] = useState(false);
  const [genCopy, setGenCopy] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const [assetTarget, setAssetTarget] = useState('product');
  const [showFonts, setShowFonts] = useState(false);
  const [error, setError] = useState('');
  const [genAiTemplate, setGenAiTemplate] = useState(false);
  const [showTypo, setShowTypo] = useState(false);
  const [history, setHistory] = useState([]);

  const prodRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/flyers/history`);
      setHistory(data.flyers || []);
    } catch {}
  };

  /* preset change */
  useEffect(() => {
    setHeadFont(preset.headFont);
    setHeadSize(preset.headSize);
    setHeadWeight(preset.headWeight);
    setHeadColor(preset.text);
    setSubColor(preset.sub);
    setCtaBg(preset.ctaBg);
    setCtaTextColor(preset.ctaText);
    setPositions({ ...preset.positions });
    setScales({ ...preset.scales });
    setSelected(null);
    if (preset.layout === 'left-aligned') setHeadAlign('left');
    else setHeadAlign('center');
  }, [preset]);

  /* ── DRAG ── */
  const onDragStart = useCallback((id, e) => {
    e.stopPropagation(); e.preventDefault();
    setSelected(id); setDragging(true);
  }, []);

  const onDragMove = useCallback((e) => {
    if (!dragging || !selected || !canvasRef.current) return;
    const r = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setPositions(p => ({
      ...p,
      [selected]: { x: Math.max(5, Math.min(95, ((cx - r.left) / r.width) * 100)), y: Math.max(4, Math.min(96, ((cy - r.top) / r.height) * 100)) },
    }));
  }, [dragging, selected]);

  const onDragEnd = useCallback(() => setDragging(false), []);

  /* ── RESIZE ── */
  const onResizeStart = useCallback((id, e) => {
    e.stopPropagation(); e.preventDefault();
    setResizing(id);
    resizeStart.current = { cx: e.clientX || e.touches?.[0]?.clientX, cy: e.clientY || e.touches?.[0]?.clientY, scale: scales[id] };
  }, [scales]);

  const onResizeMove = useCallback((e) => {
    if (!resizing || !resizeStart.current) return;
    const cx = e.clientX || e.touches?.[0]?.clientX || 0;
    const cy = e.clientY || e.touches?.[0]?.clientY || 0;
    const dx = cx - resizeStart.current.cx;
    const dy = cy - resizeStart.current.cy;
    const delta = (dx + dy) / 200;
    setScales(s => ({ ...s, [resizing]: Math.max(0.3, Math.min(3, resizeStart.current.scale + delta)) }));
  }, [resizing]);

  const onResizeEnd = useCallback(() => { setResizing(null); resizeStart.current = null; }, []);

  useEffect(() => {
    if (dragging || resizing) {
      const mv = (e) => { if (dragging) onDragMove(e); if (resizing) onResizeMove(e); };
      const up = () => { onDragEnd(); onResizeEnd(); };
      window.addEventListener('mousemove', mv);
      window.addEventListener('mouseup', up);
      window.addEventListener('touchmove', mv, { passive: false });
      window.addEventListener('touchend', up);
      return () => { window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); window.removeEventListener('touchmove', mv); window.removeEventListener('touchend', up); };
    }
  }, [dragging, resizing, onDragMove, onDragEnd, onResizeMove, onResizeEnd]);

  /* ── UPLOAD ── */
  const handleUpload = (e, target) => {
    const f = e.target.files?.[0]; if (!f) return;
    const url = URL.createObjectURL(f);
    target === 'product' ? setProductImg(url) : setLogoImg(url);
    e.target.value = '';
  };
  const onAssetSelect = (url) => assetTarget === 'product' ? setProductImg(url) : setLogoImg(url);

  /* ── AI COPY ── */
  const aiSuggest = async () => {
    setGenCopy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/flyers/generate-copy`, { template: preset.id, headline, subtext });
      if (data.copy) {
        if (data.copy.headline) setHeadline(data.copy.headline);
        if (data.copy.subtext) setSubtext(data.copy.subtext);
        if (data.copy.cta) setCta(data.copy.cta);
      }
    } catch (err) { setError(err.response?.status === 429 ? 'Daily limit reached.' : 'AI suggestion failed.'); }
    setGenCopy(false);
  };

  /* ── AI Template ── */
  // Zero Friction: pre-populate canvas from Brand Profile on first session-visit.
  // Once headline/subtext are pre-filled and a brand-matched preset is picked,
  // the user lands on a canvas that already looks like their brand.
  const handleBrandProfileLoad = (bp) => {
    if (!bp) return;
    try {
      const sessionKey = 'scl_flyer_prepopulated';
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, '1');
    } catch { /* ignore */ }
    const brand = bp.brand_name || '';
    if (!headline) {
      // Tasteful brand-derived headline; tone-aware fallback
      const candidates = [
        brand ? `${brand}` : null,
        bp.industry ? `Built for ${bp.industry}.` : null,
        brand ? `Meet ${brand}.` : null,
      ].filter(Boolean);
      if (candidates.length) setHeadline(candidates[0]);
    }
    if (!subtext && bp.description) setSubtext(String(bp.description).slice(0, 120));
    // Fire a fresh, brand-matched template — quietly
    setTimeout(() => { try { generateAiTemplate(); } catch {} }, 80);
  };

  const generateAiTemplate = async () => {
    setGenAiTemplate(true);
    try {
      const { data } = await axios.post(`${API}/api/ai/generate`, {
        prompt: `Generate a unique flyer color scheme. Return ONLY valid JSON: {"name":"creative name","bg":"hex","accent":"hex","text":"hex","sub":"rgba string","ctaBg":"hex","ctaText":"hex","headFont":"one of: playfair,montserrat,inter,bebas,oswald,space-grotesk,poppins,lora","headSize":42,"headWeight":"600","layout":"one of: centered,left-aligned,top-heavy"}. Make it visually striking. Return ONLY JSON.`,
        max_tokens: 300,
      });
      const content = data.content || data.text || '';
      // Strip markdown code fences if any, then grab the largest JSON object
      const cleaned = content.replace(/```json|```/g, '').trim();
      const first = cleaned.indexOf('{');
      const last = cleaned.lastIndexOf('}');
      let tpl = null;
      if (first !== -1 && last !== -1 && last > first) {
        try { tpl = JSON.parse(cleaned.slice(first, last + 1)); } catch {}
      }
      if (!tpl) throw new Error('Could not parse AI template');
      const layout = tpl.layout || 'centered';
      const isLeft = layout === 'left-aligned';
      const newPreset = {
        id: `ai-${Date.now()}`, name: tpl.name || 'AI Custom', layout,
        bg: tpl.bg || '#0a0a0a', accent: tpl.accent || '#c8f060', text: tpl.text || '#f5f0e8',
        sub: tpl.sub || 'rgba(245,240,232,0.5)',
        headFont: tpl.headFont || 'montserrat', headSize: tpl.headSize || 42, headWeight: tpl.headWeight || '600',
        subFont: 'dm-sans', ctaBg: tpl.ctaBg || tpl.accent || '#c8f060', ctaText: tpl.ctaText || '#080808',
        overlay: `linear-gradient(180deg, transparent 30%, ${tpl.bg || '#0a0a0a'}f0 70%)`,
        positions: isLeft
          ? { headline:{x:18,y:35}, subtext:{x:18,y:52}, cta:{x:18,y:72}, logo:{x:12,y:8}, productImg:{x:75,y:50} }
          : { headline:{x:50,y:40}, subtext:{x:50,y:56}, cta:{x:50,y:76}, logo:{x:50,y:8}, productImg:{x:50,y:50} },
        scales: { headline:1, subtext:1, cta:1, logo:1, productImg:1 },
        badge: null, decorStyle: layout === 'top-heavy' ? 'corner' : 'line',
      };
      setPreset(newPreset);
      import('sonner').then(m => m.toast.success('New template generated'));
    } catch (err) {
      import('sonner').then(m => m.toast.error(err?.response?.data?.detail || 'Template generation failed'));
    }
    setGenAiTemplate(false);
  };

  /* ── EXPORT ── */
  const save = async () => {
    if (!canvasRef.current) return;
    const hasCredits = await checkCreditsBeforeAction('flyer_generate');
    if (!hasCredits) return;
    setExporting(true); setError(''); setSelected(null);
    await new Promise(r => setTimeout(r, 80));
    try {
      const el = canvasRef.current;
      const scale = Math.max(2, exportSize.w / el.offsetWidth);
      const canvas = await html2canvas(el, { scale, backgroundColor: null, useCORS: true, allowTaint: true });
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png', 1.0));
      const fname = `flyer-${preset.id}-${Date.now()}.png`;
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fname, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file] }); await deductCreditsAfterExport('flyer_generate'); setExporting(false); return; } catch (err) { if (err.name === 'AbortError') { setExporting(false); return; } }
        }
      }
      const a = document.createElement('a'); a.download = fname; a.href = URL.createObjectURL(blob); a.click(); URL.revokeObjectURL(a.href);
      await deductCreditsAfterExport('flyer_generate');
    } catch { setError('Export failed. Try again.'); }
    setExporting(false);
  };

  /* ══════ RENDER ══════ */
  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="flyer-creator-page">
        <BrandSettingsSummary fields={['brand','color']} onLoad={handleBrandProfileLoad} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Flyer &amp; Graphics
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>
              Promotional graphics — ready to post or print.
            </p>
          </div>
          <div className="flex gap-2">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'My Designs'}
              </button>
            ))}
          </div>
        </div>

        {tab === 'create' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* ─── LEFT: Creative Brief ─── */}
            <div className="space-y-4 order-2 lg:order-1">
              {/* Content */}
              <Card title="Your Content">
                <FieldInput label="Headline" value={headline} onChange={setHeadline} placeholder="Enter your headline" testId="inp-headline" />
                <FieldInput label="Supporting Text" value={subtext} onChange={setSubtext} placeholder="Add a supporting message" testId="inp-subtext" multiline />
                <FieldInput label="Call to Action" value={cta} onChange={setCta} placeholder="Learn more" testId="inp-cta" />
                <button onClick={aiSuggest} disabled={genCopy} data-testid="ai-copy-btn"
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-medium transition-all"
                  style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.12)', opacity: genCopy ? 0.5 : 1 }}>
                  {genCopy ? <ArrowsClockwise size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />}
                  {genCopy ? 'Rewriting…' : 'Rewrite all →'}
                </button>
              </Card>

              {/* Template Style */}
              <Card title="Template Style" badge={`${PRESETS.length} styles`}>
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  {PRESETS.map(p => (
                    <button key={p.id} onClick={() => setPreset(p)} data-testid={`preset-${p.id}`}
                      className="p-2.5 rounded-lg text-left transition-all hover:scale-[1.02]"
                      style={{ background: preset.id === p.id ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.02)', border: preset.id === p.id ? '1px solid rgba(200,240,96,0.25)' : '1px solid rgba(255,255,255,0.05)' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: p.accent }} />
                        <span className="text-xs font-semibold" style={{ color: preset.id === p.id ? '#c8f060' : '#f5f0e8' }}>{p.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <button onClick={generateAiTemplate} disabled={genAiTemplate} data-testid="generate-ai-template-btn"
                  className="w-full py-2.5 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all"
                  style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.15)', color: '#c8f060' }}>
                  {genAiTemplate ? <><ArrowsClockwise size={12} className="animate-spin" /> Designing your graphic…</> : <><Sparkle size={12} weight="fill" /> Match my brand style</>}
                </button>
              </Card>

              {/* Typography (collapsible) */}
              <Card>
                <button onClick={() => setShowTypo(!showTypo)} className="flex items-center justify-between w-full" data-testid="toggle-typography">
                  <h3 className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>Typography & Colors</h3>
                  <CaretDown size={14} style={{ color: 'rgba(245,240,232,0.3)', transform: showTypo ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </button>
                <AnimatePresence>
                  {showTypo && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden">
                      <div className="pt-4 space-y-3">
                        {/* Font Selector */}
                        <div>
                          <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Headline Font</label>
                          <div className="relative">
                            <button onClick={() => setShowFonts(!showFonts)} data-testid="font-selector"
                              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs"
                              style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}>
                              <span style={{ fontFamily: fontOf(headFont).family }}>{fontOf(headFont).name}</span>
                              <CaretDown size={12} className="opacity-30" />
                            </button>
                            <AnimatePresence>
                              {showFonts && (
                                <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-4 }}
                                  className="absolute z-50 w-full mt-1 rounded-lg max-h-44 overflow-y-auto"
                                  style={{ background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.1)', boxShadow:'0 8px 32px rgba(0,0,0,0.5)' }}>
                                  {FONTS.map(f => (
                                    <button key={f.id} onClick={() => { setHeadFont(f.id); setShowFonts(false); }}
                                      className="w-full text-left px-3 py-2 text-xs hover:bg-white/5"
                                      style={{ color: headFont === f.id ? '#c8f060' : 'rgba(245,240,232,0.6)', fontFamily: f.family }}>{f.name}</button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>

                        {/* Size Slider */}
                        <div>
                          <div className="flex justify-between mb-1">
                            <label className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)' }}>Size</label>
                            <span className="text-[10px] font-mono" style={{ color:'#c8f060' }}>{headSize}px</span>
                          </div>
                          <input type="range" min={20} max={80} step={1} value={headSize} onChange={e => setHeadSize(+e.target.value)} className="w-full accent-[#c8f060]" />
                        </div>

                        {/* Weight */}
                        <div>
                          <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Weight</label>
                          <div className="flex gap-1">
                            {['400','500','600','700','800'].map(w => (
                              <button key={w} onClick={() => setHeadWeight(w)} className="flex-1 py-1.5 rounded text-[9px] transition-all"
                                style={{ background: headWeight===w ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.02)', color: headWeight===w ? '#c8f060' : 'rgba(245,240,232,0.3)', border: headWeight===w ? '1px solid rgba(200,240,96,0.2)' : '1px solid rgba(255,255,255,0.04)', fontWeight: headWeight===w ? 600 : 400 }}>
                                {w==='400'?'Reg':w==='500'?'Med':w==='600'?'Semi':w==='700'?'Bold':'Xtra'}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Alignment */}
                        <div>
                          <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Alignment</label>
                          <div className="flex gap-1">
                            {[{v:'left',I:TextAlignLeft},{v:'center',I:TextAlignCenter},{v:'right',I:TextAlignRight}].map(({v,I}) => (
                              <button key={v} onClick={() => setHeadAlign(v)} data-testid={`align-${v}`}
                                className="flex-1 flex items-center justify-center py-2 rounded-lg transition-all"
                                style={{ background: headAlign===v ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.02)', border: headAlign===v ? '1px solid rgba(200,240,96,0.2)' : '1px solid rgba(255,255,255,0.04)' }}>
                                <I size={14} style={{ color: headAlign===v ? '#c8f060' : 'rgba(245,240,232,0.3)' }} />
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Colors */}
                        <div>
                          <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Headline Color</label>
                          <ColorPicker value={headColor} onChange={setHeadColor} />
                        </div>
                        <div>
                          <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>CTA Background</label>
                          <ColorPicker value={ctaBg} onChange={setCtaBg} />
                        </div>
                        <div>
                          <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>CTA Text</label>
                          <ColorPicker value={ctaTextColor} onChange={setCtaTextColor} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>

              {/* Media */}
              <Card title="Media">
                <div className="space-y-2">
                  <MediaSlot label="Product / Background Image" img={productImg}
                    onUp={() => prodRef.current?.click()} onAssets={() => { setAssetTarget('product'); setShowAssets(true); }}
                    onRm={() => setProductImg(null)} tid="flyer-product" />
                  <MediaSlot label="Logo" img={logoImg}
                    onUp={() => logoRef.current?.click()} onAssets={() => { setAssetTarget('logo'); setShowAssets(true); }}
                    onRm={() => setLogoImg(null)} tid="flyer-logo" />
                  <input ref={prodRef} type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e,'product')} />
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e,'logo')} />
                </div>
              </Card>

              {/* Export Size */}
              <Card title="Export Format">
                <div className="flex gap-2">
                  {EXPORTS.map(s => (
                    <button key={s.id} onClick={() => setExportSize(s)} data-testid={`size-${s.id}`}
                      className="flex-1 p-3 rounded-lg text-center transition-all"
                      style={{ background: exportSize.id===s.id ? 'rgba(200,240,96,0.06)' : 'rgba(255,255,255,0.02)', border: exportSize.id===s.id ? '1px solid rgba(200,240,96,0.2)' : '1px solid rgba(255,255,255,0.05)' }}>
                      <p className="text-[10px] font-medium" style={{ color: exportSize.id===s.id ? '#c8f060' : 'rgba(245,240,232,0.5)' }}>{s.label}</p>
                      <p className="text-[9px] mt-0.5" style={{ color:'rgba(245,240,232,0.2)' }}>{s.tag}</p>
                    </button>
                  ))}
                </div>
              </Card>
            </div>

            {/* ─── RIGHT: Live Preview ─── */}
            <div className="order-1 lg:order-2">
              <div className="lg:sticky lg:top-20 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye size={16} style={{ color: 'rgba(245,240,232,0.3)' }} />
                    <span className="text-xs" style={{ color:'rgba(245,240,232,0.3)' }}>
                      {selected ? <span style={{ color: '#c8f060' }}>Editing: {selected}</span> : 'Live Preview'}
                    </span>
                  </div>
                  <span className="text-[10px] px-2 py-1 rounded" style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060' }}>
                    {exportSize.label}
                  </span>
                </div>

                {/* CANVAS */}
                <div
                  ref={canvasRef} data-testid="flyer-canvas"
                  className="relative overflow-hidden rounded-xl mx-auto select-none"
                  style={{ width:'100%', maxWidth:'520px', aspectRatio:`${exportSize.w}/${exportSize.h}`, background: preset.bg, cursor: dragging ? 'grabbing' : 'default' }}
                  onClick={() => setSelected(null)}
                >
                  {/* BG image */}
                  {productImg && (<>
                    <img src={productImg} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
                    {preset.overlay !== 'none' && <div className="absolute inset-0" style={{ background: preset.overlay }} />}
                  </>)}

                  {/* Decorative elements */}
                  {preset.decorStyle === 'line' && <div className="absolute top-0 left-0 right-0 h-[3px] z-10" style={{ background: preset.accent }} />}
                  {preset.decorStyle === 'corner' && (<>
                    <div className="absolute top-0 left-0 w-24 h-24 z-10" style={{ borderTop: `4px solid ${preset.accent}`, borderLeft: `4px solid ${preset.accent}` }} />
                    <div className="absolute bottom-0 right-0 w-24 h-24 z-10" style={{ borderBottom: `4px solid ${preset.accent}`, borderRight: `4px solid ${preset.accent}` }} />
                  </>)}
                  {preset.decorStyle === 'grid' && (
                    <div className="absolute inset-0 z-[1] pointer-events-none opacity-[0.04]"
                      style={{ backgroundImage: `linear-gradient(${preset.accent} 1px, transparent 1px), linear-gradient(90deg, ${preset.accent} 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
                  )}
                  {preset.decorStyle === 'glow' && (
                    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full z-[1] pointer-events-none"
                      style={{ background: preset.accent, filter: 'blur(80px)', opacity: 0.15 }} />
                  )}
                  {preset.decorStyle === 'wave' && (
                    <div className="absolute bottom-0 left-0 right-0 h-20 z-[1] pointer-events-none opacity-10"
                      style={{ background: `linear-gradient(180deg, transparent, ${preset.accent})` }} />
                  )}
                  {preset.decorStyle === 'gradient-blur' && (<>
                    <div className="absolute top-0 right-0 w-40 h-40 rounded-full z-[1] pointer-events-none"
                      style={{ background: '#a78bfa', filter: 'blur(60px)', opacity: 0.12 }} />
                    <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full z-[1] pointer-events-none"
                      style={{ background: '#ec4899', filter: 'blur(60px)', opacity: 0.1 }} />
                  </>)}
                  {preset.decorStyle === 'serif-line' && (
                    <div className="absolute left-[8%] top-[20%] bottom-[20%] w-[2px] z-10" style={{ background: preset.accent, opacity: 0.12 }} />
                  )}

                  {/* Badge */}
                  {preset.badge && (
                    <div className="absolute top-4 right-4 z-20 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wider"
                      style={{ background: preset.accent, color: preset.bg }}>
                      {preset.badge}
                    </div>
                  )}

                  {/* LOGO */}
                  {logoImg && (
                    <CanvasEl id="logo" pos={positions.logo} scale={scales.logo} sel={selected}
                      onDrag={onDragStart} onResize={onResizeStart} align={preset.layout === 'left-aligned' ? 'left' : 'center'}>
                      <img src={logoImg} alt="logo" draggable={false} className="object-contain" style={{ height: `${32 * scales.logo}px` }} />
                    </CanvasEl>
                  )}

                  {/* HEADLINE */}
                  <CanvasEl id="headline" pos={positions.headline} scale={scales.headline} sel={selected}
                    onDrag={onDragStart} onResize={onResizeStart} align={preset.layout === 'left-aligned' ? 'left' : 'center'}>
                    <h2 data-testid="preview-headline" style={{
                      fontFamily: fontOf(headFont).family,
                      fontSize: `${Math.min(headSize, headline.length > 40 ? 24 : headline.length > 25 ? 30 : headline.length > 15 ? Math.min(headSize, 38) : headSize) * scales.headline}px`,
                      fontWeight: headWeight, color: headColor,
                      textAlign: preset.layout === 'left-aligned' ? 'left' : headAlign,
                      lineHeight: 1.08, margin: 0,
                      maxWidth: preset.layout === 'left-aligned' ? '65%' : '85%',
                      letterSpacing: '-0.02em', wordBreak: 'break-word', overflowWrap: 'break-word',
                      textShadow: preset.bg !== '#c8f060' && preset.bg !== '#fafafa' && preset.bg !== '#f5f0e8' ? '0 2px 20px rgba(0,0,0,0.5)' : 'none',
                    }}>{headline}</h2>
                  </CanvasEl>

                  {/* SUBTEXT */}
                  <CanvasEl id="subtext" pos={positions.subtext} scale={scales.subtext} sel={selected}
                    onDrag={onDragStart} onResize={onResizeStart} align={preset.layout === 'left-aligned' ? 'left' : 'center'}>
                    <p data-testid="preview-subtext" style={{
                      fontFamily: fontOf('dm-sans').family,
                      fontSize: `${Math.min(subSize, 16) * scales.subtext}px`,
                      color: subColor,
                      textAlign: preset.layout === 'left-aligned' ? 'left' : headAlign,
                      lineHeight: 1.5, margin: 0,
                      maxWidth: preset.layout === 'left-aligned' ? '50%' : '75%',
                      letterSpacing: '0.01em',
                    }}>{subtext}</p>
                  </CanvasEl>

                  {/* CTA */}
                  <CanvasEl id="cta" pos={positions.cta} scale={scales.cta} sel={selected}
                    onDrag={onDragStart} onResize={onResizeStart} align={preset.layout === 'left-aligned' ? 'left' : 'center'}>
                    <div data-testid="preview-cta" className="font-semibold tracking-wide"
                      style={{
                        padding: `${12 * scales.cta}px ${30 * scales.cta}px`,
                        borderRadius: preset.layout === 'left-aligned' ? '8px' : '9999px',
                        background: ctaBg, color: ctaTextColor,
                        fontFamily: fontOf(headFont).family,
                        boxShadow: `0 4px 20px ${typeof ctaBg === 'string' && ctaBg.startsWith('#') ? ctaBg + '33' : 'rgba(0,0,0,0.2)'}`,
                        fontSize: `${14 * scales.cta}px`,
                      }}>{cta}</div>
                  </CanvasEl>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <button onClick={save} disabled={exporting} data-testid="flyer-save-btn"
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-all"
                    style={{ background:'#c8f060', color:'#080808', opacity: exporting ? 0.5 : 1 }}>
                    {exporting ? <ArrowsClockwise size={16} className="animate-spin" /> : <DownloadSimple size={16} weight="bold" />}
                    {exporting ? 'Exporting...' : 'Export as PNG'}
                  </button>
                </div>
                <p className="text-[10px] text-center" style={{ color: 'rgba(245,240,232,0.2)' }}>1 credit per export</p>

                {error && <p className="text-xs text-red-400 mt-2" data-testid="flyer-error">{error}</p>}
              </div>
            </div>
          </div>
        ) : (
          /* ─── HISTORY TAB ─── */
          <div data-testid="flyer-history">
            {history.length === 0 ? (
              <div className="text-center py-16">
                <ImageSquare size={48} style={{ color: 'rgba(245,240,232,0.15)' }} className="mx-auto mb-4" />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.35)' }}>No designs yet. Create your first flyer!</p>
                <button onClick={() => setTab('create')} className="mt-4 px-6 py-2.5 rounded-xl text-sm font-semibold"
                  style={{ background: '#c8f060', color: '#080808' }}>
                  Create Now
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {history.map((item, i) => (
                  <div key={i} className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="aspect-square bg-black/20 flex items-center justify-center">
                      <ImageSquare size={32} style={{ color: 'rgba(245,240,232,0.1)' }} />
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-medium truncate" style={{ color: '#f5f0e8' }}>{item.name || 'Flyer'}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>{item.created_at || ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <AssetPickerModal isOpen={showAssets} onClose={() => setShowAssets(false)} onSelect={onAssetSelect} accept="image" title="Choose from Assets" />
      </div>
    </DashboardLayout>
  );
}

/* ── Canvas Element (draggable + resizable) ── */
function CanvasEl({ id, pos, scale, sel, onDrag, onResize, children, align }) {
  const isSel = sel === id;
  const tx = align === 'left' ? '0%' : '-50%';
  return (
    <div className="absolute z-10" style={{ left:`${pos.x}%`, top:`${pos.y}%`, transform:`translate(${tx},-50%)`, cursor:'grab' }}
      onMouseDown={e => onDrag(id, e)} onTouchStart={e => onDrag(id, e)}>
      {isSel && (
        <div className="absolute -inset-2 rounded-lg pointer-events-none" style={{ border:'1.5px dashed rgba(200,240,96,0.5)', background:'rgba(200,240,96,0.03)' }}>
          <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-[#c8f060]" />
          <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#c8f060]" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full bg-[#c8f060]" />
          <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 rounded-sm cursor-nwse-resize pointer-events-auto"
            style={{ background:'#c8f060' }}
            onMouseDown={e => onResize(id, e)} onTouchStart={e => onResize(id, e)} />
        </div>
      )}
      {children}
    </div>
  );
}

/* ── Color Picker ── */
function ColorPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {COLORS.map(c => (
        <button key={c} onClick={() => onChange(c)} className="w-5 h-5 rounded-full transition-all hover:scale-110"
          style={{ background:c, border: value===c ? '2px solid #c8f060' : c==='#000000' ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent', boxShadow: value===c ? '0 0 0 2px rgba(200,240,96,0.2)' : 'none' }} />
      ))}
      <div className="relative w-5 h-5">
        <input type="color" value={value.startsWith('#')?value:'#ffffff'} onChange={e => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
        <div className="w-5 h-5 rounded-full" style={{ background:'conic-gradient(red,yellow,lime,aqua,blue,magenta,red)', border:'1px solid rgba(255,255,255,0.2)' }} />
      </div>
    </div>
  );
}

/* ── Media Slot ── */
function MediaSlot({ label, img, onUp, onAssets, onRm, tid }) {
  return (
    <div className="rounded-lg overflow-hidden" style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.05)' }} data-testid={tid}>
      {img ? (
        <div className="relative group">
          <img src={img} alt={label} className="w-full h-20 object-cover" />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button onClick={onUp} className="p-1.5 rounded bg-white/10 hover:bg-white/20"><Upload size={14} className="text-white" /></button>
            <button onClick={onAssets} className="p-1.5 rounded bg-white/10 hover:bg-white/20"><FolderOpen size={14} className="text-white" /></button>
            <button onClick={onRm} className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/40"><Trash size={14} className="text-red-400" /></button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3">
          <ImageSquare size={16} style={{ color: 'rgba(245,240,232,0.2)' }} />
          <p className="flex-1 text-[11px] font-medium" style={{ color:'rgba(245,240,232,0.4)' }}>{label}</p>
          <button onClick={onUp} className="px-2.5 py-1 rounded text-[10px] font-medium" style={{ background:'rgba(255,255,255,0.04)', color:'rgba(245,240,232,0.4)' }}>Upload</button>
          <button onClick={onAssets} data-testid={`${tid}-assets`} className="px-2.5 py-1 rounded text-[10px] font-medium" style={{ background:'rgba(200,240,96,0.05)', color:'#c8f060' }}>Assets</button>
        </div>
      )}
    </div>
  );
}
